import { AIDA_SUBSCRIPTION_TYPES, PROJECT_TYPES, URLS } from "@/constants/constants";
import { ProjectRole } from "../StepsWrapper";
import { BriefResponseData } from "./types";
import { BEGIN_PROCESS_REQUIREMENTS_BRIEF } from "@/app/(protected)/asset-generation/constant";
import { filterGuidelines } from "@/lib/utils";
import { BRIEF_INPUT_CONSTANT } from "@/app/(protected)/asset-generation/components/AddInputs/constants";

export const FILE_SIZE = "10 MB";
export const CONTENT_GEN_FILE_SIZE = "50 MB";
export const FILE_STATUS = {
  UPLOADED: "Uploaded",
  NOT_UPLOADED: "Not Uploaded",
};
export const CONTINUE_BUTTON_TEXT = "Continue";

const briefTemplateLink = "https://partners.mdlz.com/:f:/s/AIDAtraining/EhyTVJNskrxLluCL2sxlfT8BA-HtarrOL0oKb4--4HQ-rw?e=S3y6xZ";

export const PDPCopyLink =
  "https://teams.mdlz.com/:x:/r/sites/digitalshelfexcellencesupportus/Shared%20Documents/General/Training%20presentations/Digital%20Content%20Development/AIDA%20Briefs/Flywheel%20Digital%20Commerce%20Copy%20Brief_Template%20(1).xlsx?d=w2af81827f6594766a45aff85f7cbe6d3&csf=1&web=1&e=3hg9tJ";

const LifeStyleLink =
  "https://teams.mdlz.com/:x:/r/sites/digitalshelfexcellencesupportus/Shared%20Documents/General/Training%20presentations/Digital%20Content%20Development/AIDA%20Briefs/POP%20Digital%20Commerce%20Creative%20Brief.xlsx?d=wc3083ed67e0f48a6bc3d16e713f1116b&csf=1&web=1&e=MaWNc3";
const commonRequirements = [
  "Any images contained in the brief will not be parsed and used for prompting",
  "Include campaign-specific details such as: audience cohorts, geographic targets, channels, audience interests, and related topics",
  "Do not include sensitive, confidential, proprietary (e.g., patents, product recipes), unethical, or inaccurate information",
  "Follow inclusive language guidelines",
  "Brand or design style guides do not need to be included",
];

export const ERROR_MESSAGE_INVALID_FILE_NAME =
  "Filename contains invalid or potentially malicious content. Please rename the file and try again.";

export const STEP_ONE_CONSTANTS = {
  PROJECT_HEADING_ID: "uploadBriefHeading",
  FILE_SIZE_LIMIT: 10 * 1024 * 1024, // 10MB
  DS_LLM_REQUEST_SIZE_LIMIT: Number(process.env.DS_LLM_REQUEST_SIZE_LIMIT ?? 52428800), //bytes
  PROJECT_NAME_LABEL: "PROJECT NAME",
  PROJECT_TITLE: "SELECT PROJECT OPTIONS",
  // Step One requirements
  STEP_ONE_REQUIREMENTS:
    "To generate a campaign concept, please upload brief documents in PDF format. Make sure to review the brief guidelines to ensure you get the best output possible.",
  STEP_ONE_SAVE_TEXT: "To SAVE a project, you must complete all steps of the process.",
  LIFESTYLE_STEP_ONE_SAVE_TEXT: "To SAVE a project, you must upload a brief and continue to Project image list.",
  LIFESTYLE_STEP_ONE_REQUIREMENTS:
    "You can generate lifestyle images with your brand character or product by entering a description or using a brief.  When uploading a brief, make sure to use a Microsoft Excel (.xlsx) format and review the brief guidelines to ensure you get the best output possible. ",
  PDP_COPY_STEP_ONE_REQUIREMENTS:
    "To generate copy for product detail pages, please upload a brief in Microsoft Excel (.xlsx) format. Review our brief guidelines below to help ensure you get the best possible output.",
  PDP_COPY_STEP_ONE_SAVE_TEXT: "To SAVE a project, you must upload a brief and continue to the Project copy list.",
  // Accordion

  CONCEPTING_REQUIREMENTS: [
    "Upload one (1) brief document in PDF format (max 10 MB); multiple documents can be combined into a single PDF",
    {
      text: "If you don’t have a brief document, refer to the",
      link: briefTemplateLink,
      linkLabel: "Concept Brief Template",
    },
    ...commonRequirements,
  ],

  ASSETS_GENERATION_REQUIREMENTS(subscriptionType?: string) {
    const updatedCommonRequirements =
      subscriptionType === AIDA_SUBSCRIPTION_TYPES.UNIVERSAL ? commonRequirements.slice(0, -1) : commonRequirements;
    return [
      `Upload one (1) brief document in PDF format (max 10 MB); multiple documents ${subscriptionType === AIDA_SUBSCRIPTION_TYPES.UNIVERSAL ? "(including any brand or design style guides)" : ""} can be combined into a single PDF`,
      {
        text: "If you don’t have a brief document, refer to the",
        link: briefTemplateLink,
        linkLabel: "Brief Template",
      },
      ...updatedCommonRequirements,
    ];
  },

  EXCLUDE_LIFESTYLE_FILE_REQUIREMENTS_OREO_BRAZIL: ["Only base Oreo GTINs will be parsed to generate content"],

  LIFESTYLE_FILE_REQUIREMENTS(brandDisplayName: string) {
    return [
      "Upload one (1) brief document in Microsoft Excel (.xlsx) format",
      {
        text: "If you don’t have a brief document, refer to the",
        link: LifeStyleLink,
        linkLabel: "Image brief template",
      },
      "Only the first 25 reference images will be accepted, along with image notes and comments",
      `Only base ${brandDisplayName} GTINs will be parsed to generate content`,
      "Information should be unique to the campaign",
      "Do not include brand or design style guides",
      "Do not use sensitive, confidential or proprietary information (e.g., patents, product recipes)",
      "Do not input unethical or inaccurate information",
      "Always follow inclusive language guidelines",
    ];
  },
  PDP_COPY_FILE_REQUIREMENTS(brandDisplayName: string) {
    return [
      "Upload one (1) brief document in Microsoft Excel (.xlsx) format",
      {
        text: "If you don’t have a brief document, refer to the",
        link: PDPCopyLink,
        linkLabel: "Copy brief template",
      },
      "Only the first 25 reference GTINS will be accepted",
      `Only base ${brandDisplayName} GTINs will be parsed to generate content`,
      "Information should be unique to the campaign",
      "Brand or design style guides do not need to be included",
      "Do not enter sensitive, confidential or proprietary information (e.g., patents, product recipes)",
      "Do not enter unethical or inaccurate information",
      "Always follow inclusive language guidelines",
    ];
  },

  // Success
  MESSAGE_INPUT_DUPLICATE_NAME: "A project with this name already exists. We’ve updated the name below.",

  //Errors
  ERROR_MESSAGE_INPUT_EMPTY: "A project name is required. Try choosing something specific to your campaign concept.",
  ERROR_MESSAGE_UPLOAD_EMPTY: "At least one (1) PDF is required.",
  LIFESTYLE_ERROR_MESSAGE_UPLOAD_EMPTY: "You must upload one (1) Microsoft Excel (.xlsx) document.",
  LIFESTYLE_UPLOAD_FILE_FORMAT: "Please make sure your file is in Microsoft Excel (.xlsx) format.",
  ERROR_MESSAGE_UPLOAD_FILE_TYPE: "Your file must be in PDF format",
  ERROR_MESSAGE_UPLOAD_FILE_SIZE: `Your file must be less than ${FILE_SIZE}.`,
  ERROR_MESSAGE_UPLOAD_CONTENT_GEN_FILE_SIZE: `Your file must be less than ${CONTENT_GEN_FILE_SIZE}.`,
  ERROR_MESSAGE_UPLOAD_FILE_LIMIT: "Only one PDF file is allowed.",
  LIFESTYLE_ERROR_MESSAGE_UPLOAD_FILE_LIMIT: "Please upload one (1) file at a time",
  ERROR_MESSAGE_UPLOAD_FILE_TYPE_SIZE: `Your file must be in PDF format and less than ${FILE_SIZE}`,
  ERROR_MESSAGE_UPLOAD_UNSAFE_PDF:
    "Your file contains active/executable content and cannot be uploaded. Please remove any embedded scripts, actions or attachments and try again.",
  ERROR_PROJECT_CHECK_NAME: "Error checking project name.",
  ERROR_INVALID_EXCEL_ROWS: "Required columns 'GTIN' or 'Image Deliverables' are missing in the sheet.",
  ERROR_BLANK_ROW: "The sheet does not contain any data rows after the GTIN header row.",
  ERROR_GTIN_REQUIRED: "At least one of 'Image Deliverables' or 'GTIN' must be provided.",
  ERROR_BLANK_EXCEL: "Required tab is missing in the sheet.",
  ERROR_PROJECT_NAME_EXISTS: "Project name already exists, Please try different name.",
  ERROR_REQUIRED_FIELDS: "Required fields are missing in the sheet",
  API_ERROR_MESSAGE: "We're unable to upload your brief right now. Please try again later.",
  ERROR_BLANK_EXCEL_DATA: "It seems like your brief is missing some information. Please fix it and try again.",
};
export const AUDIENCE_TYPES = {
  DESIRED_TARGET: "desired target",
  LIFESTYLE: "lifestyle",
  LIFESTYLE_AND_INTERESTS: "lifestyle and interests",
  LOCATION: "location",
  CHANNEL: "channel",
} as const;

export const DESIRED_TARGET_TOOLTIP = "Provide specific attitudes, values and beliefs that describe your audience.";
export const LIFESTYLE_TOOLTIP = "Include audience interests, hobbies or pastime activities.";
export const LOCATION_TOOLTIP = "Include the locations where the generated content will appear.";
export const CHANNEL_TOOLTIP = "Indicate the channels where the generated content will appear.";

export const normalizeAudienceData = (response: BriefResponseData) => {
  return [
    {
      audience_type: AUDIENCE_TYPES.DESIRED_TARGET,
      audience_type_label: AUDIENCE_TYPES.DESIRED_TARGET,
      audience_type_summary: DESIRED_TARGET_TOOLTIP,
      feature_topics: response.cohort?.slice(0, 5) ?? [],
    },
    {
      audience_type: AUDIENCE_TYPES.LIFESTYLE,
      audience_type_label: AUDIENCE_TYPES.LIFESTYLE_AND_INTERESTS,
      audience_type_summary: LIFESTYLE_TOOLTIP,
      feature_topics: response.lifestyle?.slice(0, 5) ?? [],
    },
    {
      audience_type: AUDIENCE_TYPES.LOCATION,
      audience_type_label: AUDIENCE_TYPES.LOCATION,
      audience_type_summary: LOCATION_TOOLTIP,
      feature_topics: response.location?.slice(0, 5) ?? [],
    },
    {
      audience_type: AUDIENCE_TYPES.CHANNEL,
      audience_type_label: AUDIENCE_TYPES.CHANNEL,
      audience_type_summary: CHANNEL_TOOLTIP,
      feature_topics: response.channel?.slice(0, 5) ?? [],
    },
  ];
};

const {
  STEP_ONE_REQUIREMENTS,
  CONCEPTING_REQUIREMENTS,
  ASSETS_GENERATION_REQUIREMENTS,
  ERROR_MESSAGE_UPLOAD_EMPTY,
  LIFESTYLE_FILE_REQUIREMENTS,
  LIFESTYLE_ERROR_MESSAGE_UPLOAD_EMPTY,
  LIFESTYLE_STEP_ONE_REQUIREMENTS,
  ERROR_MESSAGE_UPLOAD_FILE_LIMIT,
  LIFESTYLE_ERROR_MESSAGE_UPLOAD_FILE_LIMIT,
  STEP_ONE_SAVE_TEXT,
  LIFESTYLE_STEP_ONE_SAVE_TEXT,
  PDP_COPY_FILE_REQUIREMENTS,
  PDP_COPY_STEP_ONE_REQUIREMENTS,
  PDP_COPY_STEP_ONE_SAVE_TEXT,
  EXCLUDE_LIFESTYLE_FILE_REQUIREMENTS_OREO_BRAZIL,
} = STEP_ONE_CONSTANTS;

export const BRANDS_REGION_LIST = {
  OREO_BRAZIL: "oreo - brazil",
  OREO_US: "oreo - us",
  CHIPS_DCOMM: "chips ahoy dcomm - us",
  OREO_DCOMM_MX: "oreo dcomm - mx",
  OREO_DCOMM_IN: "oreo_dcomm_in",
  OREO_DCOMM_AU: "oreo_dcomm_au",
};

export const getProjectType = (projectRole: ProjectRole) => {
  switch (projectRole) {
    case "concepter":
      return PROJECT_TYPES.CAMPAIGN;
    case "lifeStyle":
      return PROJECT_TYPES.PDP;
    case "pdpCopy":
      return PROJECT_TYPES.PDP_COPY;
    case "assetGeneration":
      return PROJECT_TYPES.GENERATE_ASSETS;
  }
};

export const getUrlByProjectType = (projectType?: string): string => {
  switch (projectType) {
    case PROJECT_TYPES.PDP:
      return URLS.GENERATE_LIFESTYLE;
    case PROJECT_TYPES.PDP_COPY:
      return URLS.PDP_COPY;
    case PROJECT_TYPES.GENERATE_ASSETS:
      return URLS.GENERATE_ASSETS;
    default:
      return URLS.CONCEPTING;
  }
};

export const getBrandDisplayName = (brandId: string) => {
  const { OREO_US, CHIPS_DCOMM } = BRANDS_REGION_LIST;
  switch (brandId) {
    case OREO_US:
      return "Oreo";
    case CHIPS_DCOMM:
      return "Chips Ahoy!";
    default:
      return "Oreo";
  }
};

export const getBriefGuidelines = (projectRole: ProjectRole, brand?: string | undefined, subscriptionType?: string) => {
  if (projectRole === "concepter") {
    return CONCEPTING_REQUIREMENTS;
  }

  if (projectRole === "assetGeneration") {
    return ASSETS_GENERATION_REQUIREMENTS(subscriptionType);
  }
  if (projectRole === "pdpCopy") {
    return PDP_COPY_FILE_REQUIREMENTS(getBrandDisplayName(brand as string));
  }

  if (projectRole === "lifeStyle") {
    const brandDisplayName = getBrandDisplayName(brand as string);
    if (brand?.toLowerCase() === BRANDS_REGION_LIST.OREO_BRAZIL) {
      return filterGuidelines(LIFESTYLE_FILE_REQUIREMENTS(brandDisplayName), EXCLUDE_LIFESTYLE_FILE_REQUIREMENTS_OREO_BRAZIL);
    }
    return LIFESTYLE_FILE_REQUIREMENTS(brandDisplayName);
  }

  return [];
};

export const getBriefText = (projectRole: ProjectRole) => {
  switch (projectRole) {
    case "concepter":
      return STEP_ONE_REQUIREMENTS;
    case "assetGeneration":
      return BEGIN_PROCESS_REQUIREMENTS_BRIEF;
    case "lifeStyle":
      return LIFESTYLE_STEP_ONE_REQUIREMENTS;
    case "pdpCopy":
      return PDP_COPY_STEP_ONE_REQUIREMENTS;
  }
};

export const getBriefSaveText = (projectRole: ProjectRole) => {
  if (projectRole === "concepter" || projectRole === "assetGeneration") return STEP_ONE_SAVE_TEXT;
  if (projectRole === "pdpCopy") return PDP_COPY_STEP_ONE_SAVE_TEXT;
  return LIFESTYLE_STEP_ONE_SAVE_TEXT;
};

export const getUploadType = (projectRole: ProjectRole) => {
  if (projectRole === "concepter" || projectRole === "assetGeneration") return ".pdf";
  return ".xls, .xlsx";
};

export const getDragDropText = (projectRole: ProjectRole) => {
  if (projectRole === "concepter" || projectRole === "assetGeneration") return "PDF";
  return ".xlsx";
};

export const getUploadEmptyError = (projectRole: ProjectRole) => {
  if (projectRole === "concepter" || projectRole === "assetGeneration") {
    return ERROR_MESSAGE_UPLOAD_EMPTY;
  } else {
    return LIFESTYLE_ERROR_MESSAGE_UPLOAD_EMPTY;
  }
};

export const getUploadLimitError = (projectRole: ProjectRole) => {
  if (projectRole === "concepter" || projectRole === "assetGeneration") {
    return ERROR_MESSAGE_UPLOAD_FILE_LIMIT;
  } else {
    return LIFESTYLE_ERROR_MESSAGE_UPLOAD_FILE_LIMIT;
  }
};

export const getSeoKeywordLink = (brandId: string): string => {
  if (brandId === BRANDS_REGION_LIST.CHIPS_DCOMM) {
    return "https://partners.mdlz.com/:x:/r/sites/ProjectNeuroGenAI/Shared%20Documents/3g.%20US%20Chips%20Ahoy!%20dComm%20Activation/Kickoff%20Materials/Approved%20Brand%20Materials/CHIPS%20AHOY!%20SEO%20Keywords_dComm%20Team_9.18.25.xlsx?d=w9e44d25ad7a7449aa16895a166728b99&csf=1&web=1&e=t5NZeB";
  }
  return "https://teams.mdlz.com/:x:/r/sites/digitalshelfexcellencesupportus/Shared%20Documents/General/Training%20presentations/Digital%20Content%20Development/AIDA%20Briefs/OREO%20Keywords%20-%20Top%2040.xlsx?d=w896106219b6a4e9b957714e2e931fd62&csf=1&web=1&e=PhehRj";
};

export const IMAGE_INPUT_TYPE = {
  BRIEF_ID: "useBrief",
  BRIEF_LABEL: "Use a brief",
  DESCRIPTION_ID: "enterDescription",
  DESCRIPTION_LABEL: "Enter a description",
  INPUT_TYPE_NAME: "assetInputType",
  ERROR_MESSAGE_ID: "assetTypeError",
  BRIEF_UPLOAD_SECTION_ID: "brief-upload-section",
  INPUT_ASSET_TYPE_ERROR_MESSAGE: "Please select how you want to generate assets. ",
};

export const imageInputTypeOptions = [
  {
    id: IMAGE_INPUT_TYPE.BRIEF_ID,
    label: IMAGE_INPUT_TYPE.BRIEF_LABEL,
    ariaLabel: IMAGE_INPUT_TYPE.BRIEF_LABEL,
  },
  {
    id: IMAGE_INPUT_TYPE.DESCRIPTION_ID,
    label: IMAGE_INPUT_TYPE.DESCRIPTION_LABEL,
    ariaLabel: IMAGE_INPUT_TYPE.DESCRIPTION_LABEL,
  },
];

export const projectBriefFields = [
  {
    label: STEP_ONE_CONSTANTS.PROJECT_NAME_LABEL,
    name: "projectName",
    maxLength: 150,
    required: true,
    ariaLabel: STEP_ONE_CONSTANTS.PROJECT_NAME_LABEL,
    refPassed: true,
  },
];

export const imageInputTypeField = {
  name: IMAGE_INPUT_TYPE.INPUT_TYPE_NAME,
  label: BRIEF_INPUT_CONSTANT().ASSET_CREATING_TYPE_LABEL,
  options: imageInputTypeOptions,
  useFieldset: true,
};
